import { EDisplayMode, EInitialState, EParametersMap, EShading } from "./enums";
import * as Loader from "./loader";
import { Presets } from "./presets";
import { logoCompositor } from "./logo-compositor";
import * as DynamicNoise from "./dynamic-noise";


/* === IDs ============================================================ */
const controlId = {
    PARAMETERS_MAP_TABS: "map-tabs-id",
    PRESET_SELECT: "presets-fixed-select-id",
    INPUT_IMAGE_UPLOAD: "input-image-upload-button",
    PATTERNS_SCALE: "pattern-scale-range-id",
    A_FEEDING_RANGE: "A-feeding-range-id",
    A_DIFFUSION_RANGE: "A-diffusion-range-id",
    B_KILLING_RANGE: "B-killing-range-id",
    B_DIFFUSION_RANGE: "B-diffusion-range-id",
    PICK_VALUES_BUTTON: "pick-values-button-id",

    SPEED_RANGE: "speed-range-id",
    FORCE_AMPLITUDE_RANGE: "force-amplitude-range-id",
    FORCE_SCALE_RANGE: "force-scale-range-id",
    FORCE_SPEED_RANGE: "force-speed-range-id",
    NOISE_SCALE_R_RANGE: "noise-scale-r-range-id",
    NOISE_SCALE_G_RANGE: "noise-scale-g-range-id",
    NOISE_SCALE_B_RANGE: "noise-scale-b-range-id",
    NOISE_OPACITY_RANGE: "noise-opacity-range-id",
    RANDOM_WALK_SPEED_RANGE: "random-walk-speed-range-id",
    NOISE_SCALE_R_MIN_RANGE: "noise-scale-r-min-range-id",
    NOISE_SCALE_R_MAX_RANGE: "noise-scale-r-max-range-id",
    NOISE_SCALE_G_MIN_RANGE: "noise-scale-g-min-range-id",
    NOISE_SCALE_G_MAX_RANGE: "noise-scale-g-max-range-id",
    NOISE_SCALE_B_MIN_RANGE: "noise-scale-b-min-range-id",
    NOISE_SCALE_B_MAX_RANGE: "noise-scale-b-max-range-id",
    NOISE_OPACITY_MIN_RANGE: "noise-opacity-min-range-id",
    NOISE_OPACITY_MAX_RANGE: "noise-opacity-max-range-id",
    FORCE_AMPLITUDE_MIN_RANGE: "force-amplitude-min-range-id",
    FORCE_AMPLITUDE_MAX_RANGE: "force-amplitude-max-range-id",
    FORCE_SCALE_MIN_RANGE: "force-scale-min-range-id",
    FORCE_SCALE_MAX_RANGE: "force-scale-max-range-id",
    FORCE_SPEED_MIN_RANGE: "force-speed-min-range-id",
    FORCE_SPEED_MAX_RANGE: "force-speed-max-range-id",
    FORCE_RADIAL_MIN_RANGE: "force-radial-min-range-id",
    FORCE_RADIAL_MAX_RANGE: "force-radial-max-range-id",
    
    // Walk enable checkboxes
    NOISE_SCALE_R_WALK_CHECKBOX: "noise-scale-r-walk-checkbox-id",
    NOISE_SCALE_G_WALK_CHECKBOX: "noise-scale-g-walk-checkbox-id",
    NOISE_SCALE_B_WALK_CHECKBOX: "noise-scale-b-walk-checkbox-id",
    NOISE_OPACITY_WALK_CHECKBOX: "noise-opacity-walk-checkbox-id",
    FORCE_AMPLITUDE_WALK_CHECKBOX: "force-amplitude-walk-checkbox-id",
    FORCE_SCALE_WALK_CHECKBOX: "force-scale-walk-checkbox-id",
    FORCE_SPEED_WALK_CHECKBOX: "force-speed-walk-checkbox-id",
    FORCE_RADIAL_WALK_CHECKBOX: "force-radial-walk-checkbox-id",
    
    // Live value displays
    NOISE_SCALE_R_LIVE_RANGE: "noise-scale-r-live-range-id",
    NOISE_SCALE_G_LIVE_RANGE: "noise-scale-g-live-range-id",
    NOISE_SCALE_B_LIVE_RANGE: "noise-scale-b-live-range-id",
    NOISE_OPACITY_LIVE_RANGE: "noise-opacity-live-range-id",
    FORCE_AMPLITUDE_LIVE_RANGE: "force-amplitude-live-range-id",
    FORCE_SCALE_LIVE_RANGE: "force-scale-live-range-id",
    FORCE_SPEED_LIVE_RANGE: "force-speed-live-range-id",
    FORCE_RADIAL_LIVE_RANGE: "force-radial-live-range-id",
    
    BRUSH_SIZE_RANGE: "brush-size-range-id",
    BRUSH_DISPLAY_CHECKBOX: "brush-display-checkbox-id",
    INITIAL_STATE_TABS: "initial-state-tabs-id",
    RESET_BUTTON: "reset-button-id",

    DISPLAY_MODE_TABS: "display-mode-tabs-id",
    SHADING_TABS: "shading-tabs-id",
    ZOOM_RANGE: "zoom-range-id",
    BLUR_RANGE: "blur-range-id",
    NOISE_CHECKBOX: "noise-checkbox-id",
    NOISE_AMOUNT_RANGE: "noise-amount-range-id",
    CHROMA_CHECKBOX: "chroma-checkbox-id",
    CHROMA_RANGE: "chroma-range-id",
    SCANLINE_CHECKBOX: "scanline-checkbox-id",
    SCANLINE_RANGE: "scanline-intensity-range-id",
    JITTER_CHECKBOX: "jitter-checkbox-id",
    JITTER_RANGE: "jitter-amount-range-id",
    INDICATORS_CHECKBOX: "indicators-checkbox-id",

    IMAGE_DOWNLOAD: "image-download-id",
    
    // Performance controls
    DYNAMIC_NOISE_CHECKBOX: "dynamic-noise-checkbox-id",
    LOGO_COMPOSITOR_CHECKBOX: "logo-compositor-checkbox-id",
    HIGH_FREQ_CANVAS_CHECKBOX: "high-freq-canvas-checkbox-id",
    USE_BACKGROUND_IMAGE_CHECKBOX: "use-background-image-checkbox-id",
};

type Observer = () => unknown;
type ImageUploadObserver = (image: HTMLImageElement) => unknown;

function callObservers(observers: Observer[]): void {
    for (const observer of observers) {
        observer();
    }
}

let isInValuePickingMode = false;

const updateParametersVisibility = () => {
    const map = Parameters.parametersMap;
    const displayMode = Parameters.displayMode;
    Page.Controls.setVisibility(controlId.PRESET_SELECT, map !== EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.A_FEEDING_RANGE, map !== EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.B_KILLING_RANGE, map !== EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.PICK_VALUES_BUTTON, map !== EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.PATTERNS_SCALE, map === EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.A_DIFFUSION_RANGE, map !== EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.B_DIFFUSION_RANGE, map !== EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.INPUT_IMAGE_UPLOAD, map === EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.DISPLAY_MODE_TABS, map === EParametersMap.IMAGE);
    Page.Controls.setVisibility(controlId.SHADING_TABS, displayMode === EDisplayMode.MONOCHROME);
};

function clearPreset(): void {
    Page.Select.setValue(controlId.PRESET_SELECT, null);
}
Page.Range.addObserver(controlId.A_FEEDING_RANGE, clearPreset);
Page.Range.addObserver(controlId.A_DIFFUSION_RANGE, clearPreset);
Page.Range.addObserver(controlId.B_KILLING_RANGE, clearPreset);
Page.Range.addObserver(controlId.B_DIFFUSION_RANGE, clearPreset);
// Page.Range.addObserver(controlId.SPEED_RANGE, clearPreset);
// Page.Tabs.addObserver(controlId.INITIAL_STATE_TABS, clearPreset);
// Page.Tabs.addObserver(controlId.SHADING_TABS, clearPreset);
// Page.Range.addObserver(controlId.ZOOM_RANGE, clearPreset);

export abstract class Parameters {
    public static readonly imageUploadObservers: ImageUploadObserver[] = [];
    public static readonly imageDownloadObservers: Observer[] = [];
    public static readonly canvasResizeObservers: Observer[] = [];
    public static readonly resetObservers: Observer[] = [];
    public static readonly blurChangeObservers: Observer[] = [];

    public static get parametersMap(): EParametersMap {
        if (isInValuePickingMode) {
            return EParametersMap.VALUE_PICKING;
        }
        return Page.Tabs.getValues(controlId.PARAMETERS_MAP_TABS)[0] as EParametersMap;
    }
    public static exitValuePickingMode(): void {
        isInValuePickingMode = false;
        Page.Tabs.setValues(controlId.PARAMETERS_MAP_TABS, [EParametersMap.UNIFORM]);
        updateParametersVisibility();
    }

    public static get patternsScale(): number {
        return Page.Range.getValue(controlId.PATTERNS_SCALE);
    }
    public static get AFeedingRate(): number {
        return Page.Range.getValue(controlId.A_FEEDING_RANGE);
    }
    public static set AFeedingRate(value: number) {
        Page.Range.setValue(controlId.A_FEEDING_RANGE, value);
        Page.Range.storeState(controlId.A_FEEDING_RANGE);
        clearPreset();
    }
    public static get ADiffusionRate(): number {
        return Page.Range.getValue(controlId.A_DIFFUSION_RANGE);
    }
    public static get BKillingRate(): number {
        return Page.Range.getValue(controlId.B_KILLING_RANGE);
    }
    public static set BKillingRate(value: number) {
        Page.Range.setValue(controlId.B_KILLING_RANGE, value);
        Page.Range.storeState(controlId.B_KILLING_RANGE);
        clearPreset();
    }
    public static get BDIffusionRate(): number {
        return Page.Range.getValue(controlId.B_DIFFUSION_RANGE);
    }

    public static get speed(): number {
        return Page.Range.getValue(controlId.SPEED_RANGE);
    }
    public static get forceAmplitude(): number {
        return Page.Range.getValue(controlId.FORCE_AMPLITUDE_RANGE);
    }
    public static get forceScale(): number {
        return Page.Range.getValue(controlId.FORCE_SCALE_RANGE);
    }
    public static get forceSpeed(): number {
        return Page.Range.getValue(controlId.FORCE_SPEED_RANGE);
    }
    public static get noiseScaleR(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_R_RANGE);
    }
    public static get noiseScaleG(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_G_RANGE);
    }
    public static get noiseScaleB(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_B_RANGE);
    }
    public static get noiseOpacity(): number {
        return Page.Range.getValue(controlId.NOISE_OPACITY_RANGE);
    }
    public static get randomWalkSpeed(): number {
        return Page.Range.getValue(controlId.RANDOM_WALK_SPEED_RANGE);
    }
    public static get noiseScaleRMin(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_R_MIN_RANGE);
    }
    public static get noiseScaleRMax(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_R_MAX_RANGE);
    }
    public static get noiseScaleGMin(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_G_MIN_RANGE);
    }
    public static get noiseScaleGMax(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_G_MAX_RANGE);
    }
    public static get noiseScaleBMin(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_B_MIN_RANGE);
    }
    public static get noiseScaleBMax(): number {
        return Page.Range.getValue(controlId.NOISE_SCALE_B_MAX_RANGE);
    }
    public static get noiseOpacityMin(): number {
        return Page.Range.getValue(controlId.NOISE_OPACITY_MIN_RANGE);
    }
    public static get noiseOpacityMax(): number {
        return Page.Range.getValue(controlId.NOISE_OPACITY_MAX_RANGE);
    }
    public static get forceAmplitudeMin(): number {
        return Page.Range.getValue(controlId.FORCE_AMPLITUDE_MIN_RANGE);
    }
    public static get forceAmplitudeMax(): number {
        return Page.Range.getValue(controlId.FORCE_AMPLITUDE_MAX_RANGE);
    }
    public static get forceScaleMin(): number {
        return Page.Range.getValue(controlId.FORCE_SCALE_MIN_RANGE);
    }
    public static get forceScaleMax(): number {
        return Page.Range.getValue(controlId.FORCE_SCALE_MAX_RANGE);
    }
    public static get forceSpeedMin(): number {
        return Page.Range.getValue(controlId.FORCE_SPEED_MIN_RANGE);
    }
    public static get forceSpeedMax(): number {
        return Page.Range.getValue(controlId.FORCE_SPEED_MAX_RANGE);
    }
    public static get forceRadialMin(): number {
        return Page.Range.getValue(controlId.FORCE_RADIAL_MIN_RANGE);
    }
    public static get forceRadialMax(): number {
        return Page.Range.getValue(controlId.FORCE_RADIAL_MAX_RANGE);
    }
    public static get brushSize(): number {
        return Page.Range.getValue(controlId.BRUSH_SIZE_RANGE);
    }
    public static get displayBrush(): boolean {
        return !isInValuePickingMode && Page.Checkbox.isChecked(controlId.BRUSH_DISPLAY_CHECKBOX);
    }
    public static get initialState(): EInitialState {
        if (isInValuePickingMode) {
            return EInitialState.CHECKERBOARD;
        }
        return Page.Tabs.getValues(controlId.INITIAL_STATE_TABS)[0] as EInitialState;
    }

    public static get zoom(): number {
        return Page.Range.getValue(controlId.ZOOM_RANGE);
    }
    public static get blur(): number {
        return Page.Range.getValue(controlId.BLUR_RANGE);
    }

    public static get noiseEnabled(): boolean {
        return Page.Checkbox.isChecked(controlId.NOISE_CHECKBOX);
    }
    public static get noiseAmount(): number {
        return Page.Range.getValue(controlId.NOISE_AMOUNT_RANGE);
    }
    public static get chromaEnabled(): boolean {
        return Page.Checkbox.isChecked(controlId.CHROMA_CHECKBOX);
    }
    public static get chromaticOffset(): number {
        return Page.Range.getValue(controlId.CHROMA_RANGE);
    }
    public static get scanlineEnabled(): boolean {
        return Page.Checkbox.isChecked(controlId.SCANLINE_CHECKBOX);
    }
    public static get scanlineIntensity(): number {
        return Page.Range.getValue(controlId.SCANLINE_RANGE);
    }
    public static get jitterEnabled(): boolean {
        return Page.Checkbox.isChecked(controlId.JITTER_CHECKBOX);
    }
    public static get jitterAmplitude(): number {
        return Page.Range.getValue(controlId.JITTER_RANGE);
    }

    public static get displayMode(): EDisplayMode {
        if (Parameters.parametersMap === EParametersMap.IMAGE) {
            return Page.Tabs.getValues(controlId.DISPLAY_MODE_TABS)[0] as EDisplayMode;
        } else {
            return EDisplayMode.TRICOLOR;
        }
    }

    public static get shading(): EShading {
        return Page.Tabs.getValues(controlId.SHADING_TABS)[0] as EShading;
    }

    // Performance controls
    public static get dynamicNoiseEnabled(): boolean {
        return Page.Checkbox.isChecked(controlId.DYNAMIC_NOISE_CHECKBOX);
    }
    
    public static get logoCompositorEnabled(): boolean {
        return Page.Checkbox.isChecked(controlId.LOGO_COMPOSITOR_CHECKBOX);
    }
    
    public static get highFreqCanvasEnabled(): boolean {
        return Page.Checkbox.isChecked(controlId.HIGH_FREQ_CANVAS_CHECKBOX);
    }
    
    public static get useBackgroundImage(): boolean {
        return Page.Checkbox.isChecked(controlId.USE_BACKGROUND_IMAGE_CHECKBOX);
    }

    // Function to update live value displays
    public static updateLiveValueDisplays(): void {
        if (Parameters.dynamicNoiseEnabled) {
            const currentValues = DynamicNoise.getCurrentValues();
            
            // Update the live range controls with current values (throttled)
            Parameters.updateLiveDisplayValue(controlId.NOISE_SCALE_R_LIVE_RANGE, currentValues.noiseScaleR);
            Parameters.updateLiveDisplayValue(controlId.NOISE_SCALE_G_LIVE_RANGE, currentValues.noiseScaleG);
            Parameters.updateLiveDisplayValue(controlId.NOISE_SCALE_B_LIVE_RANGE, currentValues.noiseScaleB);
            Parameters.updateLiveDisplayValue(controlId.NOISE_OPACITY_LIVE_RANGE, currentValues.noiseOpacity);
            Parameters.updateLiveDisplayValue(controlId.FORCE_AMPLITUDE_LIVE_RANGE, currentValues.forceAmplitude);
            Parameters.updateLiveDisplayValue(controlId.FORCE_SCALE_LIVE_RANGE, currentValues.forceScale);
            Parameters.updateLiveDisplayValue(controlId.FORCE_SPEED_LIVE_RANGE, currentValues.forceSpeed);
            Parameters.updateLiveDisplayValue(controlId.FORCE_RADIAL_LIVE_RANGE, currentValues.forceRadial);
        }
    }

    private static lastUpdateTimes: { [key: string]: number } = {};
    private static UPDATE_INTERVAL = 50; // Update every 50ms for smooth updates

    private static updateLiveDisplayValue(controlId: string, value: number): void {
        const now = performance.now();
        const lastUpdate = Parameters.lastUpdateTimes[controlId] || 0;
        
        if (now - lastUpdate > Parameters.UPDATE_INTERVAL) {
            try {
                Page.Range.setValue(controlId, value);
                Parameters.lastUpdateTimes[controlId] = now;
            } catch (error) {
                // Silently ignore errors if Page is not ready
            }
        }
    }
}

const callCanvasResizeObservers = () => { callObservers(Parameters.canvasResizeObservers); };
Page.Canvas.Observers.canvasResize.push(callCanvasResizeObservers);

const callResetObservers = () => { callObservers(Parameters.resetObservers); };
Page.Select.addObserver(controlId.PRESET_SELECT, callResetObservers);
Page.Button.addObserver(controlId.RESET_BUTTON, callResetObservers);
Page.Tabs.addObserver(controlId.PARAMETERS_MAP_TABS, callResetObservers);
Page.Tabs.addObserver(controlId.DISPLAY_MODE_TABS, callResetObservers);
Page.Tabs.addObserver(controlId.INITIAL_STATE_TABS, callResetObservers);
Parameters.imageUploadObservers.push(callResetObservers);

Page.Tabs.addObserver(controlId.PARAMETERS_MAP_TABS, () => {
    isInValuePickingMode = false;
    updateParametersVisibility();

    if (Parameters.parametersMap === EParametersMap.IMAGE) {
        Page.Range.setValue(controlId.ZOOM_RANGE, 1);
        Page.Range.setValue(controlId.SPEED_RANGE, 100);
        Page.Tabs.setValues(controlId.INITIAL_STATE_TABS, [EInitialState.CHECKERBOARD]);
        Page.Tabs.setValues(controlId.SHADING_TABS, [EShading.BINARY]);

        Page.Range.clearStoredState(controlId.ZOOM_RANGE);
        Page.Range.clearStoredState(controlId.SPEED_RANGE);
        Page.Tabs.clearStoredState(controlId.INITIAL_STATE_TABS);
        Page.Tabs.clearStoredState(controlId.SHADING_TABS);
        Page.Select.clearStoredState(controlId.PRESET_SELECT);
    }
});
Page.Tabs.addObserver(controlId.DISPLAY_MODE_TABS, updateParametersVisibility);
updateParametersVisibility();

const updateIndicatorsVisibility = () => {
    Page.Canvas.setIndicatorsVisibility(Page.Checkbox.isChecked(controlId.INDICATORS_CHECKBOX));
};
Page.Checkbox.addObserver(controlId.INDICATORS_CHECKBOX, updateIndicatorsVisibility);
updateIndicatorsVisibility();

Page.Button.addObserver(controlId.PICK_VALUES_BUTTON, () => {
    Page.Tabs.setValues(controlId.PARAMETERS_MAP_TABS, []);
    isInValuePickingMode = true;
    Page.Range.setValue(controlId.A_DIFFUSION_RANGE, 0.2097);
    Page.Range.setValue(controlId.B_DIFFUSION_RANGE, 0.1050);
    Page.Range.setValue(controlId.ZOOM_RANGE, 1);
    if (Parameters.speed < 100) {
        Page.Range.setValue(controlId.SPEED_RANGE, 100);
    }
    clearPreset();

    updateParametersVisibility();
    callResetObservers();
});

Page.Range.addObserver(controlId.BLUR_RANGE, () => {
    callObservers(Parameters.blurChangeObservers);
});

Page.FileControl.addUploadObserver(controlId.INPUT_IMAGE_UPLOAD, (filesList: FileList) => {
    if (filesList.length === 1) {
        const reader = new FileReader();
        reader.onload = () => {
            const image = new Image();
            image.addEventListener("load", () => {
                for (const observer of Parameters.imageUploadObservers) {
                    observer(image);
                }
            });
            image.src = reader.result as string;
        };
        reader.readAsDataURL(filesList[0]);
    }
});
{
    // Use the new LogoCompositor for dynamic logo + background generation
    const url = `./resources/gluu.png`;
    Loader.registerLoadingObject(url);

    logoCompositor.loadLogo(url).then(() => {
        Loader.registerLoadedObject(url);
        // Enable logo compositor by default for image mode
        logoCompositor.setEnabled(true);
        // LogoCompositor will automatically trigger image upload observers
    }).catch((error) => {
        console.error('Failed to load logo:', error);
        Loader.registerLoadedObject(url); // Mark as loaded even if failed
    });
}

// Add observer for logo compositor checkbox
Page.Checkbox.addObserver(controlId.LOGO_COMPOSITOR_CHECKBOX, () => {
    logoCompositor.setEnabled(Parameters.logoCompositorEnabled);
});

// Add observer for dynamic noise checkbox
Page.Checkbox.addObserver(controlId.DYNAMIC_NOISE_CHECKBOX, () => {
    DynamicNoise.setEnabled(Parameters.dynamicNoiseEnabled);
});

// Add observer for background image checkbox
Page.Checkbox.addObserver(controlId.USE_BACKGROUND_IMAGE_CHECKBOX, () => {
    // Force regeneration when background image setting changes
    if (logoCompositor.isCompositorEnabled()) {
        logoCompositor.setEnabled(false);
        setTimeout(() => logoCompositor.setEnabled(true), 10);
    }
});

Page.FileControl.addDownloadObserver(controlId.IMAGE_DOWNLOAD, () => {
    callObservers(Parameters.imageDownloadObservers);
});

function applyCurrentPreset(): void {
    if (Parameters.parametersMap === EParametersMap.UNIFORM) {
        const selectedPresetId = Page.Select.getValue(controlId.PRESET_SELECT);
        const preset = Presets[selectedPresetId];
        if (preset) {
            Page.Range.setValue(controlId.A_FEEDING_RANGE, preset.aFeeding);
            Page.Range.setValue(controlId.A_DIFFUSION_RANGE, preset.aDiffuse);
            Page.Range.setValue(controlId.B_KILLING_RANGE, preset.bKilling);
            Page.Range.setValue(controlId.B_DIFFUSION_RANGE, preset.bDiffuse);
            Page.Range.setValue(controlId.SPEED_RANGE, preset.speed);
            Page.Range.setValue(controlId.ZOOM_RANGE, preset.zoom);
            Page.Tabs.setValues(controlId.INITIAL_STATE_TABS, [preset.initialState]);
            Page.Tabs.setValues(controlId.SHADING_TABS, [preset.shading]);

            Page.Range.clearStoredState(controlId.A_FEEDING_RANGE);
            Page.Range.clearStoredState(controlId.A_DIFFUSION_RANGE);
            Page.Range.clearStoredState(controlId.B_KILLING_RANGE);
            Page.Range.clearStoredState(controlId.B_DIFFUSION_RANGE);
            Page.Range.clearStoredState(controlId.SPEED_RANGE);
            Page.Range.clearStoredState(controlId.ZOOM_RANGE);
            Page.Tabs.clearStoredState(controlId.INITIAL_STATE_TABS);
            Page.Tabs.clearStoredState(controlId.SHADING_TABS);
        }
    }
}
Page.Select.addObserver(controlId.PRESET_SELECT, () => {
    Parameters.exitValuePickingMode();
    applyCurrentPreset();
});
Page.Tabs.addObserver(controlId.PARAMETERS_MAP_TABS, applyCurrentPreset);
applyCurrentPreset();

// add fake observer to prevent touch events from moving viewport on touch devices
Page.Canvas.Observers.mouseDrag.push(() => { }); /* tslint:disable-line:no-empty */

// Observer for updating dynamic noise ranges when min/max controls change
function updateDynamicNoiseRanges(): void {
    DynamicNoise.setRanges(
        [Parameters.noiseScaleRMin, Parameters.noiseScaleRMax],
        [Parameters.noiseScaleGMin, Parameters.noiseScaleGMax], 
        [Parameters.noiseScaleBMin, Parameters.noiseScaleBMax],
        [Parameters.noiseOpacityMin, Parameters.noiseOpacityMax],
        [Parameters.forceAmplitudeMin, Parameters.forceAmplitudeMax],
        [Parameters.forceScaleMin, Parameters.forceScaleMax],
        [Parameters.forceSpeedMin, Parameters.forceSpeedMax],
        [Parameters.forceRadialMin, Parameters.forceRadialMax]
    );
}

// Setup observers for walk enable/disable checkboxes
Page.Checkbox.addObserver(controlId.NOISE_SCALE_R_WALK_CHECKBOX, () => {
    const enabled = Page.Checkbox.isChecked(controlId.NOISE_SCALE_R_WALK_CHECKBOX);
    DynamicNoise.setParameterWalkEnabled('noiseScaleR', enabled);
    if (!enabled) {
        // Set to middle of range when walk is disabled
        const min = Parameters.noiseScaleRMin;
        const max = Parameters.noiseScaleRMax;
        DynamicNoise.setStaticValue('noiseScaleR', (min + max) / 2);
    }
});

Page.Checkbox.addObserver(controlId.NOISE_SCALE_G_WALK_CHECKBOX, () => {
    const enabled = Page.Checkbox.isChecked(controlId.NOISE_SCALE_G_WALK_CHECKBOX);
    DynamicNoise.setParameterWalkEnabled('noiseScaleG', enabled);
    if (!enabled) {
        const min = Parameters.noiseScaleGMin;
        const max = Parameters.noiseScaleGMax;
        DynamicNoise.setStaticValue('noiseScaleG', (min + max) / 2);
    }
});

Page.Checkbox.addObserver(controlId.NOISE_SCALE_B_WALK_CHECKBOX, () => {
    const enabled = Page.Checkbox.isChecked(controlId.NOISE_SCALE_B_WALK_CHECKBOX);
    DynamicNoise.setParameterWalkEnabled('noiseScaleB', enabled);
    if (!enabled) {
        const min = Parameters.noiseScaleBMin;
        const max = Parameters.noiseScaleBMax;
        DynamicNoise.setStaticValue('noiseScaleB', (min + max) / 2);
    }
});

Page.Checkbox.addObserver(controlId.NOISE_OPACITY_WALK_CHECKBOX, () => {
    const enabled = Page.Checkbox.isChecked(controlId.NOISE_OPACITY_WALK_CHECKBOX);
    DynamicNoise.setParameterWalkEnabled('noiseOpacity', enabled);
    if (!enabled) {
        const min = Parameters.noiseOpacityMin;
        const max = Parameters.noiseOpacityMax;
        DynamicNoise.setStaticValue('noiseOpacity', (min + max) / 2);
    }
});

Page.Checkbox.addObserver(controlId.FORCE_AMPLITUDE_WALK_CHECKBOX, () => {
    const enabled = Page.Checkbox.isChecked(controlId.FORCE_AMPLITUDE_WALK_CHECKBOX);
    DynamicNoise.setParameterWalkEnabled('forceAmplitude', enabled);
    if (!enabled) {
        const min = Parameters.forceAmplitudeMin;
        const max = Parameters.forceAmplitudeMax;
        DynamicNoise.setStaticValue('forceAmplitude', (min + max) / 2);
    }
});

Page.Checkbox.addObserver(controlId.FORCE_SCALE_WALK_CHECKBOX, () => {
    const enabled = Page.Checkbox.isChecked(controlId.FORCE_SCALE_WALK_CHECKBOX);
    DynamicNoise.setParameterWalkEnabled('forceScale', enabled);
    if (!enabled) {
        const min = Parameters.forceScaleMin;
        const max = Parameters.forceScaleMax;
        DynamicNoise.setStaticValue('forceScale', (min + max) / 2);
    }
});

Page.Checkbox.addObserver(controlId.FORCE_SPEED_WALK_CHECKBOX, () => {
    const enabled = Page.Checkbox.isChecked(controlId.FORCE_SPEED_WALK_CHECKBOX);
    DynamicNoise.setParameterWalkEnabled('forceSpeed', enabled);
    if (!enabled) {
        const min = Parameters.forceSpeedMin;
        const max = Parameters.forceSpeedMax;
        DynamicNoise.setStaticValue('forceSpeed', (min + max) / 2);
    }
});

Page.Checkbox.addObserver(controlId.FORCE_RADIAL_WALK_CHECKBOX, () => {
    const enabled = Page.Checkbox.isChecked(controlId.FORCE_RADIAL_WALK_CHECKBOX);
    DynamicNoise.setParameterWalkEnabled('forceRadial', enabled);
    if (!enabled) {
        const min = Parameters.forceRadialMin;
        const max = Parameters.forceRadialMax;
        DynamicNoise.setStaticValue('forceRadial', (min + max) / 2);
    }
});

// Setup observers for min/max range changes
Page.Range.addObserver(controlId.NOISE_SCALE_R_MIN_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.NOISE_SCALE_R_MAX_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.NOISE_SCALE_G_MIN_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.NOISE_SCALE_G_MAX_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.NOISE_SCALE_B_MIN_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.NOISE_SCALE_B_MAX_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.NOISE_OPACITY_MIN_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.NOISE_OPACITY_MAX_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.FORCE_AMPLITUDE_MIN_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.FORCE_AMPLITUDE_MAX_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.FORCE_SCALE_MIN_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.FORCE_SCALE_MAX_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.FORCE_SPEED_MIN_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.FORCE_SPEED_MAX_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.FORCE_RADIAL_MIN_RANGE, updateDynamicNoiseRanges);
Page.Range.addObserver(controlId.FORCE_RADIAL_MAX_RANGE, updateDynamicNoiseRanges);

Page.Checkbox.addObserver(controlId.DYNAMIC_NOISE_CHECKBOX, () => {
    DynamicNoise.setEnabled(Parameters.dynamicNoiseEnabled);
    // Initialize ranges when dynamic noise is enabled
    if (Parameters.dynamicNoiseEnabled) {
        updateDynamicNoiseRanges();
    }
});

// Initialize checkbox states and ranges when the page loads
function initializeControls(): void {
    // Set initial checkbox states to match DynamicNoise defaults
    Page.Checkbox.setChecked(controlId.NOISE_SCALE_R_WALK_CHECKBOX, DynamicNoise.getParameterWalkEnabled('noiseScaleR'));
    Page.Checkbox.setChecked(controlId.NOISE_SCALE_G_WALK_CHECKBOX, DynamicNoise.getParameterWalkEnabled('noiseScaleG'));
    Page.Checkbox.setChecked(controlId.NOISE_SCALE_B_WALK_CHECKBOX, DynamicNoise.getParameterWalkEnabled('noiseScaleB'));
    Page.Checkbox.setChecked(controlId.NOISE_OPACITY_WALK_CHECKBOX, DynamicNoise.getParameterWalkEnabled('noiseOpacity'));
    Page.Checkbox.setChecked(controlId.FORCE_AMPLITUDE_WALK_CHECKBOX, DynamicNoise.getParameterWalkEnabled('forceAmplitude'));
    Page.Checkbox.setChecked(controlId.FORCE_SCALE_WALK_CHECKBOX, DynamicNoise.getParameterWalkEnabled('forceScale'));
    Page.Checkbox.setChecked(controlId.FORCE_SPEED_WALK_CHECKBOX, DynamicNoise.getParameterWalkEnabled('forceSpeed'));
    Page.Checkbox.setChecked(controlId.FORCE_RADIAL_WALK_CHECKBOX, DynamicNoise.getParameterWalkEnabled('forceRadial'));
    
    // Initialize ranges
    updateDynamicNoiseRanges();
}

// Call initialization when the module loads
setTimeout(initializeControls, 100); // Small delay to ensure Page object is available
